import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ASSETS } from "@/lib/assets";
import { Menu, X, Sparkles, LogOut } from "lucide-react";

export default function Header() {
  const { t, lang, setLang, user, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { to: "/instructions", label: t.nav.how },
    { to: "/library", label: t.nav.library },
    { to: "/pricing", label: t.nav.pricing },
    ...(user ? [{ to: "/profile", label: t.nav.readings }] : []),
    ...(user?.is_admin ? [{ to: "/admin", label: "Admin" }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const LangToggle = ({ id = "" }) => (
    <div className="flex items-center rounded-full border border-amber-400/50 bg-purple-950/60 p-0.5 text-xs shadow-[0_0_14px_rgba(245,158,11,0.18)]" data-testid={`lang-toggle${id}`} aria-label="Language">
      <button
        data-testid={`lang-toggle-en${id}`}
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 rounded-full font-semibold transition-all ${lang === "en" ? "btn-gold" : "text-amber-200/80 hover:text-amber-100"}`}
      >
        EN
      </button>
      <button
        data-testid={`lang-toggle-hi${id}`}
        onClick={() => setLang("hi")}
        className={`px-3 py-1.5 rounded-full font-semibold transition-all ${lang === "hi" ? "btn-gold" : "text-amber-200/80 hover:text-amber-100"}`}
      >
        हिंदी
      </button>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 glass hairline-gold">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between gap-6 h-16">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group shrink-0">
          <img src={ASSETS.logo} alt="AstroFriend" className="h-9 w-9 rounded-full ring-1 ring-amber-400/50 object-cover" />
          <span className="font-serif text-xl font-bold text-gradient-gold tracking-wide">AstroFriend</span>
        </Link>

        <nav className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 px-4">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={`text-sm whitespace-nowrap transition-colors hover:text-amber-300 ${
                location.pathname === l.to ? "text-amber-300" : "text-slate-300"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <LangToggle />
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/profile" data-testid="header-avatar" className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full ring-1 ring-amber-400/50" />
                ) : (
                  <span className="h-8 w-8 rounded-full bg-purple-700 grid place-items-center text-sm">{user.name?.[0]}</span>
                )}
              </Link>
              <button onClick={handleLogout} data-testid="logout-btn" className="text-slate-400 hover:text-amber-300">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-btn" className="btn-ghost-gold rounded-full px-4 py-1.5 text-sm">
              {t.nav.login}
            </Link>
          )}
          <button
            onClick={() => navigate(user ? "/upload" : "/login")}
            data-testid="header-readpalm-btn"
            className="btn-gold rounded-full px-4 py-1.5 text-sm flex items-center gap-1.5"
          >
            <Sparkles size={15} /> {t.nav.readPalm}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LangToggle id="-mobile" />
          <button className="text-amber-200" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-strong px-4 py-4 space-y-3 border-t border-amber-500/15" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-slate-200 py-1">
              {l.label}
            </Link>
          ))}
          <div className="flex items-center justify-end pt-2">
            {user ? (
              <button onClick={handleLogout} className="btn-ghost-gold rounded-full px-4 py-1.5 text-sm">
                {t.nav.logout}
              </button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost-gold rounded-full px-4 py-1.5 text-sm">
                {t.nav.login}
              </Link>
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate(user ? "/upload" : "/login"); }}
            className="btn-gold w-full rounded-full px-4 py-2 text-sm mt-2"
          >
            {t.nav.readPalm}
          </button>
        </div>
      )}
    </header>
  );
}
